import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  input,
  output,
  signal,
} from '@angular/core';

import { CheckboxComponent } from '../../checkbox/checkbox.component';
import { SelectComponent, SelectOption } from '../select.component';

/**
 * Responsible for representing an option
 * It accepts custom content
 *
 * ```
 * <smw-option [option]="option">
 *  <div>
 *    <smw-icon [icon]="checkIcon"> {{ option.label }}
 *  </div>
 * </smw-option>
 *
 * If no content is provided, it fallbacks to the option's label
 *
 * ```
 * <smw-option [option]="{ id: 'a', label: 'Option A' }" />
 * ```
 * Generated DOM: <smw-option>Option A</smw-option>
 *
 */
@Component({
  selector: 'smw-select-option',
  templateUrl: 'option.component.html',
  styleUrls: ['option.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CheckboxComponent],
})
export class SelectOptionComponent<T = unknown> implements AfterViewInit {
  constructor() {
    // Use effect to emit when isChecked changes
    effect(() => {
      if (this.isReady()) {
        this.checkedChange.emit(this.isChecked());
      }
    });
  }

  checkedChange = output<boolean>();
  option = input.required<SelectOption<T>>();

  selected = input(false);
  disabled = input(false);
  parent = signal<SelectComponent<T> | undefined>(undefined);
  isReady = signal(false);

  isMultiple = computed(() => this.parent()?.multiple());
  isChecked = computed(() => {
    return (this.isMultiple() && this.parent()?.isSelected(this.option().value)) ?? false;
  });

  ngAfterViewInit(): void {
    this.isReady.set(true);
  }

  register(parent: SelectComponent<T>) {
    this.parent.set(parent);
  }

  @HostListener('click', ['$event']) private onClick(event: MouseEvent) {
    event?.stopImmediatePropagation();
    this.parent()?.selectOption(this.option());
  }
}
